import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../api";
import { useExploration } from "../exploration/useExploration";
import { ErrorBanner, Spinner } from "../components/common";
import { dateTime } from "../lib/format";

/**
 * The exploratory analysis workspace.
 *
 * Deliberately not a step of the workflow. Steps 1 to 3 produce a result that
 * can be signed off and exported; nothing on this screen can, and putting it
 * in the same numbered row would say otherwise. It is reached from a link on
 * the curate screen, and in every state it is drawn inside its own container
 * with its own colour and a masthead naming what the space is — so a figure
 * from it, seen over a shoulder or in a screenshot, does not read as a result
 * of the approved workflow.
 *
 * A scientist describes an analysis in words, an agent writes a Python program
 * against a snapshot of this run, and the program runs. What comes back is the
 * program, what it printed, whatever figures it saved and a plain-language
 * account of what was done. All four are shown: the summary alone is a claim,
 * and the code is what makes the claim checkable.
 *
 * No analysis happens here (ADR-0004). This file renders server-produced text
 * and server-produced images and computes nothing, which matters more on this
 * screen than on any other — the content is arbitrary, so the only way it can
 * be trusted is if the browser had no hand in it.
 */

const SUGGESTIONS = [
  "What data is available in this run?",
  "Refit one compound with the top of the curve left free and compare it with the approved fit",
  "Plot all three conditions for one compound on the same axis",
  "Which concentration point has the widest replicate spread?",
];

/**
 * The masthead, present in every state including the greyed-out one.
 *
 * This used to be an amber warning banner, which was wrong in two ways. It
 * looked like something had gone wrong — amber on this product means a QC flag
 * or an excluded point, and borrowing that colour for a screen that is working
 * exactly as intended spends a signal the assay needs. And a banner is a strip
 * above a page that otherwise looks like every other page, so the moment it
 * scrolled off, this screen was indistinguishable from the approved workflow.
 *
 * What replaces it says the same thing structurally instead: the workspace has
 * its own colour and its own hatched edge, and this block sits at the top of it
 * naming what the space is. The claim a scientist must not miss — that nothing
 * here is reportable — is a tag beside the title rather than a wall of prose,
 * so it is still there once the words have been read a hundred times.
 */
function Masthead({ stale }) {
  return (
    <div className="explore-masthead" role="note">
      <div className="explore-masthead-title">
        <h1>Exploratory workspace</h1>
        <span className="explore-tag">Not an approved result</span>
      </div>
      <p>
        Describe any analysis of this run in words, and an agent writes a program that runs
        against a snapshot of it. Nothing on this screen changes the run, its curation or its
        exports, and nothing produced here is validated or can be reported.
      </p>
      {stale && (
        <p className="explore-stale">
          This run has been curated since this session was opened, so the answers below
          describe data that has moved.
        </p>
      )}
    </div>
  );
}

/** What the session was pinned to, which is what makes an answer re-runnable. */
function Provenance({ provenance, stale }) {
  if (!provenance) return null;
  return (
    <div className="explore-provenance">
      <span className={stale ? "pill warning" : "pill neutral"}>
        {stale ? "Out of date" : "Current"}
      </span>
      <span className="muted">
        {provenance.n_observations} measurements · {provenance.n_fits} fits ·{" "}
        {provenance.n_exclusions} excluded · analysis {provenance.analysis_version}
        {provenance.taken_at && ` · taken ${dateTime(provenance.taken_at)}`}
      </span>
    </div>
  );
}

/**
 * One program: what it was for, what it did, and the code itself.
 *
 * The purpose is the agent's own account, stated before the program ran; the
 * outcome is what the runner observed. Shown as two separate lines because
 * nothing checks that a program does what its purpose claims, and a purpose
 * that does not match the code below it is worth noticing.
 */
function Execution({ execution, runId, artifacts }) {
  const [open, setOpen] = useState(false);
  const figures = artifacts.filter((a) => execution.artifact_ids.includes(a.id));

  return (
    <div className="explore-execution">
      <p className="explore-purpose">{execution.purpose}</p>
      <p className="muted explore-outcome">
        {execution.outcome}
        {execution.timed_out && " It was stopped before it finished."}
        {execution.truncated && " Its output was too long to keep in full and was cut short."}
      </p>

      {execution.output && <pre className="explore-output">{execution.output}</pre>}
      {!execution.ok && execution.error_output && (
        <pre className="explore-output explore-error">{execution.error_output}</pre>
      )}

      {figures.map((figure) => (
        <figure key={figure.id} className="explore-figure">
          <img src={api.explorationArtifactUrl(runId, figure.id)} alt={figure.name} />
          <figcaption className="muted">{figure.name}</figcaption>
        </figure>
      ))}

      <button type="button" className="link" onClick={() => setOpen((was) => !was)}>
        {open ? "Hide the program" : "Show the program"}
      </button>
      {open && <pre className="explore-code">{execution.code}</pre>}
    </div>
  );
}

/**
 * Split an answer into prose and fenced blocks, in order.
 *
 * The agent writes markdown, and a printed table arrives inside a ``` fence.
 * Rendered as one paragraph that is unreadable: HTML collapses every newline,
 * so a table of nine fitted curves becomes a single run-on line and the fence
 * markers show up as literal backticks in the middle of a sentence.
 *
 * This splits on the fences and nothing else. Each segment's text is passed
 * through exactly as it arrived; what happens to a prose segment afterwards
 * is `Prose`'s job, below. A fitted IC50 is formatted once, by the server,
 * and re-formatting it anywhere in this file is what criterion 2 of spec 0003
 * forbids -- nothing past this point may reflow, pad or round a character.
 *
 * An unterminated fence — a truncated answer — yields its remainder as a block
 * rather than swallowing it.
 */
export function segments(content) {
  const parts = String(content ?? "").split(/```[^\n]*\n?/);
  return parts
    .map((text, i) => ({ code: i % 2 === 1, text }))
    // A fence at the very start or end leaves an empty prose part. Blocks are
    // kept whatever they contain: an empty one means the agent printed nothing
    // and hiding that would be a kindness to the wrong party.
    .filter((segment) => segment.code || segment.text.trim() !== "");
}

/**
 * `` `text` `` -> a `<code>` element, everything else untouched.
 *
 * This is the only place a character of the agent's prose is treated as
 * anything other than literal text, and it never touches an HTML string --
 * `split` on a literal backtick, handed back to React as plain string
 * children. That is what keeps it safe to point at model-authored content:
 * there is no path from a character in this answer to a tag, an attribute or
 * a script, whatever the agent -- or a program's stdout, which becomes part
 * of what the agent sees next -- puts inside the backticks.
 */
function inlineCode(text) {
  return text.split(/`([^`]*)`/).map((part, i) => (i % 2 === 1 ? <code key={i}>{part}</code> : part));
}

/**
 * A scientist's account of a data table read as prose: a line naming the
 * file, indented lines under it naming its columns, its row count, whether
 * curated exclusions are present. Rendered a line at a time it is a wall of
 * hyphens and backticks; that is the second thing this screen got wrong,
 * once fenced tables were handled (see `segments` above).
 *
 * Deliberately narrow, on purpose the same way `segments` is: a line
 * beginning with `-` (at any indent) is a list item, blank lines separate
 * blocks, and everything else is a paragraph. No headings, no numbered
 * lists, no emphasis, no links -- none of those appear in what this agent is
 * asked to produce, and adding them widens what could misrender rather than
 * what needs to. Indent is read only as one boolean, nested or not, because
 * that is the only depth the agent's own output ever uses.
 */
export function Prose({ text }) {
  const blocks = [];
  let paragraph = [];
  let items = null;

  const flushParagraph = () => {
    if (paragraph.length) blocks.push({ list: false, text: paragraph.join(" ") });
    paragraph = [];
  };
  const flushList = () => {
    if (items) blocks.push({ list: true, items });
    items = null;
  };

  for (const line of text.split("\n")) {
    const item = line.match(/^(\s*)-\s+(.*)$/);
    if (item) {
      flushParagraph();
      items = items || [];
      items.push({ nested: item[1].length >= 2, text: item[2] });
    } else if (line.trim() === "") {
      flushParagraph();
      flushList();
    } else {
      flushList();
      paragraph.push(line.trim());
    }
  }
  flushParagraph();
  flushList();

  return blocks.map((block, i) =>
    block.list ? (
      <ul key={i} className="explore-list">
        {block.items.map((it, j) => (
          <li key={j} className={it.nested ? "explore-list-nested" : undefined}>
            {inlineCode(it.text)}
          </li>
        ))}
      </ul>
    ) : (
      <p key={i}>{inlineCode(block.text)}</p>
    )
  );
}

function Answer({ content }) {
  return segments(content).map((segment, i) =>
    segment.code ? (
      <pre key={i} className="explore-output explore-said">
        {segment.text.replace(/\n$/, "")}
      </pre>
    ) : (
      <Prose key={i} text={segment.text.trim()} />
    )
  );
}

function Turn({ message, runId, artifacts }) {
  if (message.role === "user") {
    return <p className="explore-question">{message.content}</p>;
  }
  return (
    <div className="explore-answer">
      {(message.executions || []).map((execution, i) => (
        <Execution
          key={`execution-${i}`}
          execution={execution}
          runId={runId}
          artifacts={artifacts}
        />
      ))}
      <Answer content={message.content} />
      {message.grounded === false && (
        <p className="muted explore-refusal" role="status">
          Answer withheld: it quoted a number no program printed.
        </p>
      )}
    </div>
  );
}

/** The greyed-out state. The route exists and explains itself. */
function Unavailable({ reason, sessions, runId, onOpen }) {
  return (
    <div className="card explore-unavailable">
      <h2>Exploratory analysis is not available</h2>
      <p>{reason}</p>
      <p className="muted">
        Steps 1 to 3 are unaffected. <Link to={`/runs/${runId}/curate`}>Curate and export</Link>{" "}
        works as it always has.
      </p>
      {sessions.length > 0 && (
        <>
          <p className="muted">
            Sessions recorded while it was available are still readable:
          </p>
          <SessionList sessions={sessions} onOpen={onOpen} currentId={null} />
        </>
      )}
    </div>
  );
}

/**
 * The sessions on this run, as a table.
 *
 * It was a bulleted list with the counts trailing each title as muted prose,
 * which stopped being readable at about four sessions: nothing lined up, so
 * finding the one with the figures in it meant reading every line. A run
 * accumulates these, and what a scientist scans for — when it was opened, who
 * by, whether its snapshot still matches the run — are columns.
 *
 * `Messages` is the stored message count, questions and answers together, not
 * a number this file derives from it. The snapshot column is the same
 * current/out-of-date pill the open session carries above its log, so the two
 * cannot disagree about what "out of date" looks like.
 */
function SessionList({ sessions, onOpen, currentId }) {
  return (
    <div className="table-scroll explore-sessions">
      <table>
        <thead>
          <tr>
            <th>Session</th>
            <th>Opened</th>
            <th>By</th>
            <th className="num">Messages</th>
            <th className="num">Outputs</th>
            <th>Snapshot</th>
          </tr>
        </thead>
        <tbody>
          {sessions.map((session) => {
            const current = session.id === currentId;
            return (
              <tr key={session.id} className={current ? "explore-current" : undefined}>
                <td>
                  <button
                    type="button"
                    className="link"
                    // The open row is marked for a screen reader too, not only
                    // by the tint: this is a list of things you switch between.
                    aria-current={current ? "true" : undefined}
                    onClick={() => onOpen(session.id)}
                  >
                    {session.title || `Session ${session.id.slice(0, 8)}`}
                  </button>
                </td>
                <td>{dateTime(session.created_at)}</td>
                <td>{session.created_by || "—"}</td>
                <td className="num">{session.n_messages}</td>
                <td className="num">{session.n_artifacts}</td>
                <td>
                  <span className={session.stale ? "pill warning" : "pill neutral"}>
                    {session.stale ? "Out of date" : "Current"}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function ExploreScreen() {
  const { runId } = useParams();
  const {
    availability,
    sessions,
    current,
    busy,
    starting,
    error,
    start,
    open,
    ask,
    dismissError,
  } = useExploration(runId);
  const [question, setQuestion] = useState("");

  const submit = async (event) => {
    event.preventDefault();
    const text = question;
    setQuestion("");
    await ask(text);
  };

  /* Every state is inside the same container. It is what makes the screen
     look unlike the approved workflow at a glance, and a state that fell
     outside it -- the greyed-out one especially, which is the one a scientist
     meets first -- would look like an ordinary page reporting an ordinary
     failure. */
  if (availability.available === null) {
    return (
      <div className="explore-workspace">
        <Masthead />
        <Spinner label="Opening the workspace…" />
      </div>
    );
  }

  if (availability.available === false) {
    return (
      <div className="explore-workspace">
        <Masthead />
        <Unavailable
          reason={availability.reason}
          sessions={sessions}
          runId={runId}
          onOpen={open}
        />
      </div>
    );
  }

  return (
    <div className="explore-workspace">
      <Masthead stale={Boolean(current?.stale)} />
      <ErrorBanner error={error} onDismiss={dismissError} />

      <div className="card explore-sessions-card">
        <h2>Sessions</h2>
        {sessions.length === 0 ? (
          <p className="muted">
            Nothing has been explored on this run yet. A session records every question,
            every program and every figure, and is pinned to the run as it stands now.
          </p>
        ) : (
          <SessionList sessions={sessions} onOpen={open} currentId={current?.id} />
        )}
        <div className="button-row">
          <button type="button" className="primary" disabled={starting} onClick={() => start()}>
            {starting ? "Opening…" : "Start a session"}
          </button>
        </div>
      </div>

      {current && (
        <div className="card explore-session">
          <Provenance provenance={current.provenance} stale={current.stale} />

          {current.messages.length === 0 && (
            <div className="explore-empty">
              <p>Describe the analysis you want. It can be anything the data supports.</p>
              <div className="copilot-suggestions">
                {SUGGESTIONS.map((suggestion) => (
                  <button
                    key={suggestion}
                    type="button"
                    className="chip"
                    disabled={busy}
                    onClick={() => ask(suggestion)}
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="explore-log">
            {current.messages.map((message, i) => (
              <Turn
                key={`turn-${i}`}
                message={message}
                runId={runId}
                artifacts={current.artifacts || []}
              />
            ))}
            {busy && (
              <p className="muted" role="status">
                <Spinner label="Writing and running a program…" />
              </p>
            )}
          </div>

          <form className="explore-ask" onSubmit={submit}>
            <textarea
              rows={3}
              value={question}
              disabled={busy}
              placeholder="Describe an analysis…"
              aria-label="Describe an analysis"
              onChange={(event) => setQuestion(event.target.value)}
            />
            <button type="submit" className="primary" disabled={busy || !question.trim()}>
              Run
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
